import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  ArrowLeft, Users, Download, RefreshCw, CheckCircle, XCircle, 
  Clock, FileText, Brain, Target, BarChart3, AlertTriangle, 
  User, Calendar, TrendingUp, Award, Zap, ThumbsUp, ThumbsDown, HelpCircle
} from 'lucide-react';
import { toast } from 'react-hot-toast';
import Button from '../../components/ui/Button';
import Card from '../../components/ui/Card';
import Loader from '../../components/ui/Loader';
import procesoService from '../../services/procesoService';
import usuarioExamenService from '../../services/usuarioExamenService';
import discService from '../../services/discService';
import ipvService from '../../services/ipvService';
import usuarioService from '../../services/usuarioService';
import { generarReportePDF, generarReporteDISC, generarReporteCombinado } from '../../utils/pdfGenerator';
import { generarPDFIpv } from '../../utils/pdfGeneratorIpv';
import useAuthStore from '../../store/authStore';

// Colores corporativos
const COLORES = {
  naranja: '#fe7f2d',
  verde: '#297373'
};

// Helper para normalizar puntajes Raven (si vienen multiplicados por 10)
const normalizarPuntajeRaven = (puntaje, puntajeMax) => {
  let pReal = puntaje || 0;
  let pMaxReal = puntajeMax || 12;
  
  // Si el puntaje máximo es mayor a 12, viene multiplicado por 10
  if (pMaxReal > 12) {
    pReal = Math.round(pReal / 10);
    pMaxReal = Math.round(pMaxReal / 10);
  }
  
  return { puntaje: pReal, puntajeMax: pMaxReal };
};

export default function ProcesoDetalle() {
  const { id } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const { user } = useAuthStore();
  const [loading, setLoading] = useState(true);
  const [proceso, setProceso] = useState(null);
  const [usuariosData, setUsuariosData] = useState([]);
  const [filtro, setFiltro] = useState('todos'); // todos, completos, pendientes

  // Detectar si estamos en admin o supervisor
  const isAdmin = location.pathname.startsWith('/admin');
  const baseRoute = isAdmin ? '/admin/procesos' : '/supervisor/procesos';

  useEffect(() => {
    if (id) {
      cargarDatosProceso();
    }
  }, [id]);

  const cargarDatosProceso = async () => {
    try {
      setLoading(true);
      
      // Obtener datos del proceso
      const procesoData = await procesoService.getProcesoById(id);
      setProceso(procesoData);
      
      // Obtener usuarios del proceso
      const usuariosProceso = await procesoService.getUsuariosPorProceso(id);
      
      // Para cada usuario, obtener el estado de sus exámenes
      const usuariosConExamenes = await Promise.all(
        usuariosProceso.map(async (usuario) => {
          try {
            // Estado de exámenes Raven
            let ravenCompletado = false;
            let ravenData = null;
            try {
              const examenesUsuario = await usuarioExamenService.obtenerExamenesUsuario(usuario.usuarioId);
              const ravenExamen = examenesUsuario.find(e => e.estado === 'FINALIZADO' || e.estado === 'COMPLETADO');
              if (ravenExamen) {
                ravenCompletado = true;
                ravenData = ravenExamen;
              }
            } catch (err) {
              // Sin exámenes Raven
            }

            // Estado de Test DISC
            let discCompletado = false;
            let discData = null;
            try {
              const testsDisc = await discService.getTestsCompletadosPorUsuario(usuario.usuarioId);
              if (testsDisc && testsDisc.length > 0) {
                discCompletado = true;
                discData = testsDisc[0];
              }
            } catch (err) {
              // Sin tests DISC
            }

            // Estado de Examen IPV
            let ipvCompletado = false;
            let ipvData = null;
            try {
              const examenesIpv = await ipvService.listarExamenes();
              const ipvUsuario = examenesIpv.find(e => e.usuarioId === usuario.usuarioId && e.estado === 'FINALIZADO');
              if (ipvUsuario) {
                ipvCompletado = true;
                ipvData = ipvUsuario;
              }
            } catch (err) {
              // Sin exámenes IPV
            }

            return {
              ...usuario,
              examenes: {
                raven: { completado: ravenCompletado, data: ravenData },
                disc: { completado: discCompletado, data: discData },
                ipv: { completado: ipvCompletado, data: ipvData }
              },
              totalCompletados: [ravenCompletado, discCompletado, ipvCompletado].filter(Boolean).length,
              pendientes: [
                !ravenCompletado ? 'Raven' : null,
                !discCompletado ? 'DISC' : null,
                !ipvCompletado ? 'IPV' : null
              ].filter(Boolean)
            };
          } catch (err) {
            return {
              ...usuario,
              examenes: {
                raven: { completado: false, data: null },
                disc: { completado: false, data: null },
                ipv: { completado: false, data: null }
              },
              totalCompletados: 0,
              pendientes: ['Raven', 'DISC', 'IPV']
            };
          }
        })
      );

      setUsuariosData(usuariosConExamenes);
    } catch (error) {
      console.error('Error cargando proceso:', error);
      toast.error('Error al cargar datos del proceso');
    } finally {
      setLoading(false);
    }
  };

  // Calcular métricas
  const metricas = {
    total: usuariosData.length,
    completosTotal: usuariosData.filter(u => u.totalCompletados === 3).length,
    ravenCompletos: usuariosData.filter(u => u.examenes.raven.completado).length,
    discCompletos: usuariosData.filter(u => u.examenes.disc.completado).length,
    ipvCompletos: usuariosData.filter(u => u.examenes.ipv.completado).length
  };

  // Filtrar usuarios
  const usuariosFiltrados = usuariosData.filter(u => {
    if (filtro === 'completos') return u.totalCompletados === 3;
    if (filtro === 'pendientes') return u.totalCompletados < 3;
    return true;
  });

  // Descargar PDFs
  const handleDescargarRavenPDF = async (e, usuario, ravenData) => {
    if (e) {
      e.preventDefault();
      e.stopPropagation();
    }
    try {
      toast.loading('Generando PDF Raven...', { id: 'pdf-raven' });
      
      const datosUsuario = {
        nombre: usuario.usuarioNombre,
        dni: usuario.usuarioDni,
        proceso: proceso?.nombre || 'Proceso de Selección',
        fechaInicio: ravenData.fechaInicio ? new Date(ravenData.fechaInicio).toLocaleString('es-PE') : 'N/A',
        fechaFin: ravenData.fechaFinalizacion ? new Date(ravenData.fechaFinalizacion).toLocaleString('es-PE') : 'N/A',
        duracion: ravenData.tiempoTotalMinutos ? `${ravenData.tiempoTotalMinutos} min` : 'N/A',
        // Fechas raw para calcular duración si es necesario
        fechaInicioRaw: ravenData.fechaInicio || null,
        fechaFinRaw: ravenData.fechaFinalizacion || null
      };
      
      const datosExamen = { nombre: 'Test de Raven - Matrices Progresivas' };
      
      // Normalizar puntajes
      const { puntaje: puntajeReal, puntajeMax: puntajeMaxReal } = normalizarPuntajeRaven(
        ravenData.puntajeObtenido, 
        ravenData.puntajeMaximo
      );
      
      const resultado = {
        respuestasCorrectas: puntajeReal,
        respuestasIncorrectas: puntajeMaxReal - puntajeReal,
        totalPreguntas: puntajeMaxReal,
        porcentajeAcierto: puntajeMaxReal ? (puntajeReal / puntajeMaxReal) * 100 : 0,
        puntosObtenidos: puntajeReal,
        nivelRendimiento: ravenData.porcentajeCompletado >= 75 ? 'NORMAL_SUPERIOR' : 
                         ravenData.porcentajeCompletado >= 50 ? 'NORMAL_PROMEDIO' : 'INFERIOR'
      };
      
      await generarReportePDF(datosUsuario, datosExamen, resultado);
      toast.success('PDF Raven generado', { id: 'pdf-raven' });
    } catch (error) {
      console.error('Error:', error);
      toast.error('Error al generar PDF', { id: 'pdf-raven' });
    }
  };

  const handleDescargarDiscPDF = async (e, usuario, discData) => {
    if (e) {
      e.preventDefault();
      e.stopPropagation();
    }
    try {
      toast.loading('Generando PDF DISC...', { id: 'pdf-disc' });
      await generarReporteDISC(discData, { nombre: usuario.usuarioNombre, dni: usuario.usuarioDni });
      toast.success('PDF DISC generado', { id: 'pdf-disc' });
    } catch (error) {
      console.error('Error:', error);
      toast.error('Error al generar PDF', { id: 'pdf-disc' });
    }
  };

  const handleDescargarIpvPDF = async (e, usuario, ipvData) => {
    if (e) {
      e.preventDefault();
      e.stopPropagation();
    }
    try {
      toast.loading('Generando PDF IPV...', { id: 'pdf-ipv' });
      
      if (ipvData.ipvEvaluacionId) {
        const resultado = await ipvService.obtenerResultadoInterpretado(ipvData.ipvEvaluacionId);
        resultado.usuarioNombre = usuario.usuarioNombre;
        resultado.nombrePostulante = usuario.usuarioNombre;
        await generarPDFIpv(resultado);
        toast.success('PDF IPV generado', { id: 'pdf-ipv' });
      } else {
        toast.error('No hay evaluación IPV disponible', { id: 'pdf-ipv' });
      }
    } catch (error) {
      console.error('Error:', error);
      toast.error('Error al generar PDF', { id: 'pdf-ipv' });
    }
  };

  // Descargar todos los PDFs de un usuario (separados)
  const handleDescargarTodosPDFs = async (e, usuario) => {
    if (e) {
      e.preventDefault();
      e.stopPropagation();
    }
    const { examenes } = usuario;
    let descargados = 0;

    if (examenes.raven.completado && examenes.raven.data) {
      await handleDescargarRavenPDF(null, usuario, examenes.raven.data);
      descargados++;
    }
    if (examenes.disc.completado && examenes.disc.data) {
      await handleDescargarDiscPDF(null, usuario, examenes.disc.data);
      descargados++;
    }
    if (examenes.ipv.completado && examenes.ipv.data) {
      await handleDescargarIpvPDF(null, usuario, examenes.ipv.data);
      descargados++;
    }

    if (descargados === 0) {
      toast.error('Este usuario no tiene exámenes completados');
    }
  };

  // Descargar PDF combinado (los 3 en uno)
  const handleDescargarPDFCombinado = async (e, usuario) => {
    if (e) {
      e.preventDefault();
      e.stopPropagation();
    }

    const { examenes } = usuario;

    if (usuario.totalCompletados === 0) {
      toast.error('Este usuario no tiene exámenes completados');
      return;
    }

    try {
      toast.loading('Generando reporte integral...', { id: 'pdf-combinado' });

      // Obtener fechas del Raven si está completado
      const ravenData = examenes.raven.data;
      
      const datosUsuario = {
        nombre: usuario.usuarioNombre,
        dni: usuario.usuarioDni,
        proceso: proceso?.nombre || 'Proceso de Selección',
        // Agregar fechas del Raven para el PDF
        fechaInicio: ravenData?.fechaInicio ? new Date(ravenData.fechaInicio).toLocaleString('es-PE') : 'N/A',
        fechaFin: ravenData?.fechaFinalizacion ? new Date(ravenData.fechaFinalizacion).toLocaleString('es-PE') : 'N/A',
        duracion: ravenData?.tiempoTotalMinutos ? `${ravenData.tiempoTotalMinutos} min` : 'N/A',
        // Fechas raw para calcular duración si es necesario
        fechaInicioRaw: ravenData?.fechaInicio || null,
        fechaFinRaw: ravenData?.fechaFinalizacion || null
      };

      // Preparar datos de Raven
      let datosRaven = null;
      if (examenes.raven.completado && examenes.raven.data) {
        const { puntaje, puntajeMax } = normalizarPuntajeRaven(
          examenes.raven.data.puntajeObtenido,
          examenes.raven.data.puntajeMaximo
        );
        const porcentaje = puntajeMax ? (puntaje / puntajeMax) * 100 : 0;
        datosRaven = {
          puntaje,
          puntajeMax,
          porcentaje,
          nivel: porcentaje >= 75 ? 'Normal Superior' : 
                 porcentaje >= 50 ? 'Normal Promedio' : 'Inferior'
        };
      }

      // Preparar datos de DISC
      let datosDisc = null;
      if (examenes.disc.completado && examenes.disc.data) {
        datosDisc = examenes.disc.data;
      }

      // Preparar datos de IPV
      let datosIpv = null;
      if (examenes.ipv.completado && examenes.ipv.data) {
        try {
          if (examenes.ipv.data.ipvEvaluacionId) {
            datosIpv = await ipvService.obtenerResultadoInterpretado(examenes.ipv.data.ipvEvaluacionId);
          }
        } catch (err) {
          console.error('Error obteniendo IPV:', err);
        }
      }

      await generarReporteCombinado(datosUsuario, datosRaven, datosDisc, datosIpv);
      toast.success('Reporte integral generado', { id: 'pdf-combinado' });

    } catch (error) {
      console.error('Error:', error);
      toast.error('Error al generar reporte', { id: 'pdf-combinado' });
    }
  };

  // Cambiar estado de aptitud del usuario
  const handleCambiarApto = async (e, usuario, nuevoApto) => {
    if (e) {
      e.preventDefault();
      e.stopPropagation();
    }
    try {
      toast.loading('Actualizando estado...', { id: 'apto-update' });
      await usuarioService.cambiarApto(usuario.usuarioId, nuevoApto);
      
      // Actualizar el estado local
      setUsuariosData(prev => prev.map(u => 
        u.usuarioId === usuario.usuarioId 
          ? { ...u, apto: nuevoApto }
          : u
      ));
      
      toast.success(`Postulante marcado como ${nuevoApto ? 'APTO' : 'NO APTO'}`, { id: 'apto-update' });
    } catch (error) {
      console.error('Error cambiando apto:', error);
      toast.error('Error al actualizar estado', { id: 'apto-update' });
    }
  };

  // Componente Badge de Aptitud
  const BadgeApto = ({ apto }) => {
    if (apto === true) {
      return (
        <span className="inline-flex items-center gap-1 px-3 py-1 rounded-full text-xs font-bold bg-emerald-100 text-emerald-700 border border-emerald-300">
          <ThumbsUp className="w-3 h-3" />
          APTO
        </span>
      );
    }
    if (apto === false) {
      return (
        <span className="inline-flex items-center gap-1 px-3 py-1 rounded-full text-xs font-bold bg-red-100 text-red-700 border border-red-300">
          <ThumbsDown className="w-3 h-3" />
          NO APTO
        </span>
      );
    }
    return (
      <span className="inline-flex items-center gap-1 px-3 py-1 rounded-full text-xs font-bold bg-amber-100 text-amber-700 border border-amber-300">
        <HelpCircle className="w-3 h-3" />
        PENDIENTE
      </span>
    );
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-96">
        <Loader size="lg" />
      </div>
    );
  }

  if (!proceso) {
    return (
      <div className="text-center py-12">
        <p className="text-neutral-500">Proceso no encontrado</p>
        <Button onClick={() => navigate(baseRoute)} className="mt-4">
          Volver a Procesos
        </Button>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center gap-4">
        <button
          onClick={() => navigate(baseRoute)}
          className="p-2 hover:bg-neutral-100 rounded-lg transition-colors"
        >
          <ArrowLeft className="w-6 h-6 text-neutral-600" />
        </button>
        <div className="flex-1">
          <h1 className="text-3xl font-bold text-neutral-800">{proceso.nombre}</h1>
          <p className="text-neutral-600 mt-1">{proceso.descripcion}</p>
        </div>
        <Button variant="outline" onClick={cargarDatosProceso} className="gap-2">
          <RefreshCw className="w-4 h-4" />
          Actualizar
        </Button>
      </div>

      {/* Métricas */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-4">
        {/* Total Usuarios */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="bg-white rounded-xl border-2 p-5 shadow-sm"
          style={{ borderColor: COLORES.verde }}
        >
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-neutral-500">Total Postulantes</p>
              <p className="text-3xl font-bold mt-1" style={{ color: COLORES.verde }}>{metricas.total}</p>
            </div>
            <div className="p-3 rounded-xl" style={{ backgroundColor: `${COLORES.verde}15` }}>
              <Users className="w-6 h-6" style={{ color: COLORES.verde }} />
            </div>
          </div>
        </motion.div>

        {/* Completos */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.1 }}
          className="bg-white rounded-xl border-2 border-emerald-500 p-5 shadow-sm"
        >
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-neutral-500">100% Completos</p>
              <p className="text-3xl font-bold text-emerald-600 mt-1">{metricas.completosTotal}</p>
              <p className="text-xs text-emerald-600 mt-1">
                {metricas.total > 0 ? ((metricas.completosTotal / metricas.total) * 100).toFixed(0) : 0}% del total
              </p>
            </div>
            <div className="p-3 bg-emerald-50 rounded-xl">
              <Award className="w-6 h-6 text-emerald-600" />
            </div>
          </div>
        </motion.div>

        {/* Raven */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.2 }}
          className="bg-white rounded-xl border-2 p-5 shadow-sm"
          style={{ borderColor: COLORES.naranja }}
        >
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-neutral-500">Test Raven</p>
              <p className="text-3xl font-bold mt-1" style={{ color: COLORES.naranja }}>{metricas.ravenCompletos}</p>
              <p className="text-xs mt-1" style={{ color: COLORES.naranja }}>
                {metricas.total - metricas.ravenCompletos} pendientes
              </p>
            </div>
            <div className="p-3 rounded-xl" style={{ backgroundColor: `${COLORES.naranja}15` }}>
              <Target className="w-6 h-6" style={{ color: COLORES.naranja }} />
            </div>
          </div>
        </motion.div>

        {/* DISC */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.3 }}
          className="bg-white rounded-xl border-2 border-purple-500 p-5 shadow-sm"
        >
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-neutral-500">Test DISC</p>
              <p className="text-3xl font-bold text-purple-600 mt-1">{metricas.discCompletos}</p>
              <p className="text-xs text-purple-600 mt-1">
                {metricas.total - metricas.discCompletos} pendientes
              </p>
            </div>
            <div className="p-3 bg-purple-50 rounded-xl">
              <Brain className="w-6 h-6 text-purple-600" />
            </div>
          </div>
        </motion.div>

        {/* IPV */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.4 }}
          className="bg-white rounded-xl border-2 border-indigo-500 p-5 shadow-sm"
        >
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-neutral-500">Examen IPV</p>
              <p className="text-3xl font-bold text-indigo-600 mt-1">{metricas.ipvCompletos}</p>
              <p className="text-xs text-indigo-600 mt-1">
                {metricas.total - metricas.ipvCompletos} pendientes
              </p>
            </div>
            <div className="p-3 bg-indigo-50 rounded-xl">
              <TrendingUp className="w-6 h-6 text-indigo-600" />
            </div>
          </div>
        </motion.div>
      </div>

      {/* Barra de progreso general */}
      <Card className="p-5">
        <div className="flex items-center justify-between mb-3">
          <h3 className="font-semibold text-neutral-800">Progreso General del Proceso</h3>
          <span className="text-sm font-medium" style={{ color: COLORES.verde }}>
            {metricas.total > 0 ? ((metricas.completosTotal / metricas.total) * 100).toFixed(1) : 0}% completado
          </span>
        </div>
        <div className="h-4 bg-neutral-100 rounded-full overflow-hidden">
          <motion.div
            initial={{ width: 0 }}
            animate={{ width: `${metricas.total > 0 ? (metricas.completosTotal / metricas.total) * 100 : 0}%` }}
            transition={{ duration: 1, ease: "easeOut" }}
            className="h-full rounded-full"
            style={{ background: `linear-gradient(90deg, ${COLORES.verde}, ${COLORES.naranja})` }}
          />
        </div>
        <div className="flex justify-between mt-2 text-xs text-neutral-500">
          <span>{metricas.completosTotal} de {metricas.total} postulantes han completado todos los exámenes</span>
          <span>{metricas.total - metricas.completosTotal} pendientes</span>
        </div>
      </Card>

      {/* Filtros */}
      <div className="flex gap-2">
        <button
          onClick={() => setFiltro('todos')}
          className={`px-4 py-2 rounded-lg font-medium text-sm transition-all ${
            filtro === 'todos' 
              ? 'text-white shadow-md' 
              : 'bg-white text-neutral-600 border border-neutral-200 hover:bg-neutral-50'
          }`}
          style={filtro === 'todos' ? { backgroundColor: COLORES.verde } : {}}
        >
          Todos ({metricas.total})
        </button>
        <button
          onClick={() => setFiltro('completos')}
          className={`px-4 py-2 rounded-lg font-medium text-sm transition-all ${
            filtro === 'completos' 
              ? 'bg-emerald-500 text-white shadow-md' 
              : 'bg-white text-neutral-600 border border-neutral-200 hover:bg-neutral-50'
          }`}
        >
          <CheckCircle className="w-4 h-4 inline mr-1" />
          Completos ({metricas.completosTotal})
        </button>
        <button
          onClick={() => setFiltro('pendientes')}
          className={`px-4 py-2 rounded-lg font-medium text-sm transition-all ${
            filtro === 'pendientes' 
              ? 'text-white shadow-md' 
              : 'bg-white text-neutral-600 border border-neutral-200 hover:bg-neutral-50'
          }`}
          style={filtro === 'pendientes' ? { backgroundColor: COLORES.naranja } : {}}
        >
          <Clock className="w-4 h-4 inline mr-1" />
          Pendientes ({metricas.total - metricas.completosTotal})
        </button>
      </div>

      {/* Lista de Usuarios */}
      <div className="space-y-4">
        <AnimatePresence>
          {usuariosFiltrados.length === 0 ? (
            <Card className="p-12 text-center">
              <Users className="w-16 h-16 text-neutral-300 mx-auto mb-4" />
              <p className="text-neutral-500">No hay postulantes en este filtro</p>
            </Card>
          ) : (
            usuariosFiltrados.map((usuario, index) => (
              <motion.div
                key={usuario.usuarioId}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -20 }}
                transition={{ delay: index * 0.05 }}
              >
                <Card className="p-0 overflow-hidden hover:shadow-lg transition-shadow">
                  <div className="p-5">
                    <div className="flex items-start justify-between">
                      {/* Info del usuario */}
                      <div className="flex items-center gap-4">
                        <div 
                          className="w-14 h-14 rounded-xl flex items-center justify-center text-white font-bold text-lg shadow-md"
                          style={{ 
                            background: usuario.totalCompletados === 3 
                              ? `linear-gradient(135deg, #10b981, #059669)` 
                              : `linear-gradient(135deg, ${COLORES.naranja}, ${COLORES.verde})` 
                          }}
                        >
                          {usuario.usuarioNombre?.charAt(0) || 'U'}
                        </div>
                        <div>
                          <div className="flex items-center gap-3">
                            <h3 className="font-bold text-lg text-neutral-800">{usuario.usuarioNombre}</h3>
                            <BadgeApto apto={usuario.apto} />
                          </div>
                          <div className="flex items-center gap-3 text-sm text-neutral-500 mt-1">
                            <span className="flex items-center gap-1">
                              <FileText className="w-4 h-4" />
                              DNI: {usuario.usuarioDni}
                            </span>
                            <span className="flex items-center gap-1">
                              <Calendar className="w-4 h-4" />
                              {usuario.fechaAsignacion ? new Date(usuario.fechaAsignacion).toLocaleDateString('es-PE') : 'N/A'}
                            </span>
                          </div>
                        </div>
                      </div>

                      {/* Progreso, aptitud y acciones */}
                      <div className="flex items-center gap-4">
                        {/* Botones de Aptitud (solo si apto es null) */}
                        {usuario.apto === null && (
                          <div className="flex gap-2">
                            <Button
                              type="button"
                              size="sm"
                              onClick={(e) => handleCambiarApto(e, usuario, true)}
                              className="gap-1 bg-emerald-600 hover:bg-emerald-700 text-white"
                            >
                              <ThumbsUp className="w-4 h-4" />
                              APTO
                            </Button>
                            <Button
                              type="button"
                              size="sm"
                              onClick={(e) => handleCambiarApto(e, usuario, false)}
                              className="gap-1 bg-red-600 hover:bg-red-700 text-white"
                            >
                              <ThumbsDown className="w-4 h-4" />
                              NO APTO
                            </Button>
                          </div>
                        )}

                        {/* Indicador de progreso */}
                        <div className="text-center">
                          <div className="flex items-center gap-1">
                            {[1, 2, 3].map((num) => (
                              <div
                                key={num}
                                className={`w-3 h-3 rounded-full ${
                                  usuario.totalCompletados >= num ? 'bg-emerald-500' : 'bg-neutral-200'
                                }`}
                              />
                            ))}
                          </div>
                          <p className="text-xs text-neutral-500 mt-1">{usuario.totalCompletados}/3 exámenes</p>
                        </div>

                        {/* Botones de descarga */}
                        {usuario.totalCompletados > 0 && (
                          <div className="flex gap-2">
                            {/* PDF Combinado (Integral) */}
                            <Button
                              type="button"
                              size="sm"
                              onClick={(e) => handleDescargarPDFCombinado(e, usuario)}
                              className="gap-1"
                              style={{ backgroundColor: COLORES.verde }}
                            >
                              <FileText className="w-4 h-4" />
                              PAQUETE GLOBAL
                            </Button>
                            {/* PDFs Separados */}
                            <Button
                              type="button"
                              variant="outline"
                              size="sm"
                              onClick={(e) => handleDescargarTodosPDFs(e, usuario)}
                              className="gap-1"
                            >
                              <Download className="w-4 h-4" />
                              PAQUETE INDIVIDUAL
                            </Button>
                          </div>
                        )}
                      </div>
                    </div>

                    {/* Estado de exámenes */}
                    <div className="grid grid-cols-3 gap-4 mt-5">
                      {/* Raven */}
                      <div 
                        className={`p-4 rounded-xl border-2 transition-all ${
                          usuario.examenes.raven.completado 
                            ? 'border-emerald-200 bg-emerald-50' 
                            : 'border-neutral-200 bg-neutral-50'
                        }`}
                      >
                        <div className="flex items-center justify-between mb-2">
                          <div className="flex items-center gap-2">
                            <Target className={`w-5 h-5 ${usuario.examenes.raven.completado ? 'text-emerald-600' : 'text-neutral-400'}`} />
                            <span className="font-semibold text-neutral-800">Raven</span>
                          </div>
                          {usuario.examenes.raven.completado ? (
                            <CheckCircle className="w-5 h-5 text-emerald-500" />
                          ) : (
                            <XCircle className="w-5 h-5 text-neutral-300" />
                          )}
                        </div>
                        {usuario.examenes.raven.completado ? (
                          <>
                            <p className="text-sm text-emerald-700 mb-2">
                              Puntaje: {(() => {
                                const { puntaje, puntajeMax } = normalizarPuntajeRaven(
                                  usuario.examenes.raven.data?.puntajeObtenido,
                                  usuario.examenes.raven.data?.puntajeMaximo
                                );
                                return `${puntaje}/${puntajeMax}`;
                              })()}
                            </p>
                            <button
                              type="button"
                              onClick={(e) => handleDescargarRavenPDF(e, usuario, usuario.examenes.raven.data)}
                              className="w-full py-2 px-3 text-sm font-medium rounded-lg transition-all flex items-center justify-center gap-1"
                              style={{ backgroundColor: COLORES.naranja, color: 'white' }}
                            >
                              <Download className="w-4 h-4" /> PDF
                            </button>
                          </>
                        ) : (
                          <p className="text-sm text-neutral-500 flex items-center gap-1">
                            <Clock className="w-4 h-4" /> Pendiente
                          </p>
                        )}
                      </div>

                      {/* DISC */}
                      <div 
                        className={`p-4 rounded-xl border-2 transition-all ${
                          usuario.examenes.disc.completado 
                            ? 'border-purple-200 bg-purple-50' 
                            : 'border-neutral-200 bg-neutral-50'
                        }`}
                      >
                        <div className="flex items-center justify-between mb-2">
                          <div className="flex items-center gap-2">
                            <Brain className={`w-5 h-5 ${usuario.examenes.disc.completado ? 'text-purple-600' : 'text-neutral-400'}`} />
                            <span className="font-semibold text-neutral-800">DISC</span>
                          </div>
                          {usuario.examenes.disc.completado ? (
                            <CheckCircle className="w-5 h-5 text-purple-500" />
                          ) : (
                            <XCircle className="w-5 h-5 text-neutral-300" />
                          )}
                        </div>
                        {usuario.examenes.disc.completado ? (
                          <>
                            <p className="text-sm text-purple-700 mb-2">
                              Perfil: {usuario.examenes.disc.data?.perfilResultado || 'N/A'}
                            </p>
                            <button
                              type="button"
                              onClick={(e) => handleDescargarDiscPDF(e, usuario, usuario.examenes.disc.data)}
                              className="w-full py-2 px-3 bg-purple-600 text-white text-sm font-medium rounded-lg hover:bg-purple-700 transition-all flex items-center justify-center gap-1"
                            >
                              <Download className="w-4 h-4" /> PDF
                            </button>
                          </>
                        ) : (
                          <p className="text-sm text-neutral-500 flex items-center gap-1">
                            <Clock className="w-4 h-4" /> Pendiente
                          </p>
                        )}
                      </div>

                      {/* IPV */}
                      <div 
                        className={`p-4 rounded-xl border-2 transition-all ${
                          usuario.examenes.ipv.completado 
                            ? 'border-indigo-200 bg-indigo-50' 
                            : 'border-neutral-200 bg-neutral-50'
                        }`}
                      >
                        <div className="flex items-center justify-between mb-2">
                          <div className="flex items-center gap-2">
                            <TrendingUp className={`w-5 h-5 ${usuario.examenes.ipv.completado ? 'text-indigo-600' : 'text-neutral-400'}`} />
                            <span className="font-semibold text-neutral-800">IPV</span>
                          </div>
                          {usuario.examenes.ipv.completado ? (
                            <CheckCircle className="w-5 h-5 text-indigo-500" />
                          ) : (
                            <XCircle className="w-5 h-5 text-neutral-300" />
                          )}
                        </div>
                        {usuario.examenes.ipv.completado ? (
                          <>
                            <p className="text-sm text-indigo-700 mb-2">
                              Completado ✓
                            </p>
                            <button
                              type="button"
                              onClick={(e) => handleDescargarIpvPDF(e, usuario, usuario.examenes.ipv.data)}
                              className="w-full py-2 px-3 bg-indigo-600 text-white text-sm font-medium rounded-lg hover:bg-indigo-700 transition-all flex items-center justify-center gap-1"
                            >
                              <Download className="w-4 h-4" /> PDF
                            </button>
                          </>
                        ) : (
                          <p className="text-sm text-neutral-500 flex items-center gap-1">
                            <Clock className="w-4 h-4" /> Pendiente
                          </p>
                        )}
                      </div>
                    </div>

                    {/* Alerta de pendientes */}
                    {usuario.pendientes.length > 0 && (
                      <div 
                        className="mt-4 p-3 rounded-lg flex items-center gap-2"
                        style={{ backgroundColor: `${COLORES.naranja}15` }}
                      >
                        <AlertTriangle className="w-5 h-5 flex-shrink-0" style={{ color: COLORES.naranja }} />
                        <p className="text-sm" style={{ color: COLORES.naranja }}>
                          <span className="font-medium">Pendiente:</span> {usuario.pendientes.join(', ')}
                        </p>
                      </div>
                    )}
                  </div>
                </Card>
              </motion.div>
            ))
          )}
        </AnimatePresence>
      </div>
    </div>
  );
}
